import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import {
  AnotacaoType,
  AssinaturaType,
  ButtonType,
  OptionsHelper,
  showError,
} from '_metronic/helpers'
import {
  Button,
  FormCheckBox,
  FormInput,
  FormRadio,
  FormSelect,
  FormTextArea,
  IOption,
} from 'app/components'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import {
  MESES_LME,
  examesDeApac,
  gerarApac,
  gerarLme,
  medicamentosDeLme,
} from '../../core/documentosSus'
import { PrescricaoView } from '../../core/_model'
import styles from '../../Prescricao.module.scss'
import { dadosSusSchema } from '../../validation/dadosSusSchema'
import { useRotulos } from '../comum/useRotulos'

type IProps = {
  aberto: boolean
  /** Prescrições assinadas que pedem LME e as que pedem APAC (vazio esconde o bloco). */
  prescricoesLme: PrescricaoView[]
  prescricoesApac: PrescricaoView[]
  tipo?: AssinaturaType
  atendimento?: AtendimentoView
  /** Relê as prescrições do backend: roda sempre, para o que já foi gerado não ser refeito. */
  onRecarregar: () => Promise<void>
  onGerado: () => void
  onDepois: () => void
}

const anotacao = (atendimento: AtendimentoView | undefined, ...tipos: AnotacaoType[]) =>
  tipos
    .map((tipo) => atendimento?.anotacoes?.find((a) => a.tipo === tipo)?.anotacao)
    .find(Boolean) ?? ''

/**
 * Dados para gerar LME e APAC com a receita já assinada (o backend não aceita antes). A anamnese
 * vem do atendimento; a APAC pode reaproveitar o CID e a anamnese do LME. "Gerar depois" deixa
 * os dois como ação pendente no resultado.
 */
export const DadosSusModal: FC<IProps> = ({
  aberto,
  prescricoesLme,
  prescricoesApac,
  tipo,
  atendimento,
  onRecarregar,
  onGerado,
  onDepois,
}) => {
  const { t } = useRotulos()
  const temLme = prescricoesLme.length > 0
  const temApac = prescricoesApac.length > 0
  const examesApac = useMemo(() => prescricoesApac.flatMap(examesDeApac), [prescricoesApac])
  const medicamentosLme = useMemo(() => prescricoesLme.flatMap(medicamentosDeLme), [prescricoesLme])

  const formik = useFormik({
    initialValues: {
      cidLme: undefined as IOption<number> | undefined,
      anamneseLme: anotacao(atendimento, AnotacaoType.LME_Anamnese, AnotacaoType.Anamnese),
      mesesLme: Object.fromEntries(
        medicamentosLme.map((prescrito) => [
          String(prescrito.id),
          Array<string | number>(MESES_LME).fill(''),
        ])
      ),
      temTratamento: null as boolean | null,
      tratamento: anotacao(atendimento, AnotacaoType.LME_Tratamento),
      modeloTermo: undefined as IOption<number> | undefined,
      reusar: temLme,
      cidApac: undefined as IOption<number> | undefined,
      anamneseApac: anotacao(atendimento, AnotacaoType.APAC_Anamnese, AnotacaoType.Anamnese),
      quantidades: Object.fromEntries(examesApac.map((exame) => [String(exame.id), ''])),
    },
    enableReinitialize: true,
    validationSchema: dadosSusSchema(t, {
      lme: temLme,
      apac: temApac,
      medicamentosLme: medicamentosLme.map((prescrito) => prescrito.id),
      examesApac: examesApac.map((exame) => exame.id),
    }),
    onSubmit: async (valores) => {
      if (!tipo) return
      try {
        if (temLme && valores.cidLme) {
          await gerarLme(
            prescricoesLme,
            {
              cid: Number(valores.cidLme.value),
              anamnese: valores.anamneseLme.trim(),
              temTratamento: !!valores.temTratamento,
              tratamentoPrevio: valores.tratamento.trim() || undefined,
              modeloTermo: Number(valores.modeloTermo?.value) || undefined,
              // A validação garante meses a partir do 1º e sem buraco: os vazios são só o fim.
              quantidades: Object.fromEntries(
                Object.entries(valores.mesesLme).map(([id, meses]) => [
                  Number(id),
                  meses.filter((mes) => String(mes).trim() !== '').map(Number),
                ])
              ),
            },
            tipo
          )
        }
        if (temApac) {
          const cid = valores.reusar ? valores.cidLme : valores.cidApac
          await gerarApac(
            prescricoesApac,
            {
              cid: Number(cid?.value),
              anamnese: (valores.reusar ? valores.anamneseLme : valores.anamneseApac).trim(),
              quantidades: Object.fromEntries(
                Object.entries(valores.quantidades).map(([id, quantidade]) => [
                  Number(id),
                  Number(quantidade),
                ])
              ),
            },
            tipo
          )
        }
        await onRecarregar()
        onGerado()
      } catch (error) {
        // Parte pode ter saído (ex.: LME sim, APAC não): recarrega para a próxima tentativa só
        // pedir o que falta.
        await onRecarregar().catch(() => undefined)
        showError(error)
      }
    },
  })

  // Modelos de termo vinculados ao CID do LME (só busca com o CID escolhido).
  const idCidLme = formik.values.cidLme?.value
  const parametrosModelo = useMemo(
    () => ({ idPrescricao: prescricoesLme[0]?.id, idCid: idCidLme }),
    [prescricoesLme, idCidLme]
  )

  const rotuloSubmit =
    temLme && temApac
      ? t('prescricao.sus.gerar_ambos')
      : temLme
      ? t('prescricao.sus.gerar_lme')
      : t('prescricao.sus.gerar_apac')

  const nomes = (prescricoes: PrescricaoView[], campo: 'lme' | 'apac') =>
    prescricoes
      .flatMap((p) =>
        campo === 'lme'
          ? (p.medicamentos ?? []).filter((m) => m.medicamento?.lme).map((m) => m.medicamento?.nome)
          : examesDeApac(p).map((e) => e.exame?.nome)
      )
      .filter(Boolean)
      .join(', ')

  return (
    <Modal
      show={aberto}
      onHide={onDepois}
      centered
      size='lg'
      contentClassName={styles.tema}
      aria-labelledby='sus-titulo'
    >
      <form onSubmit={formik.handleSubmit} noValidate>
        <Modal.Header closeButton closeLabel={t('common.close')}>
          <div className='d-flex flex-column'>
            <Modal.Title as='h2' id='sus-titulo' className='fs-4 fw-bold'>
              {t('prescricao.sus.modal_titulo')}
            </Modal.Title>
            <span className={`fs-7 ${styles.textoSecundario}`}>
              {t('prescricao.sus.modal_subtitulo')}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body className='d-flex flex-column gap-5'>
          {temLme && (
            <fieldset className={styles.caixaLme}>
              <legend className='float-start w-100 d-flex flex-wrap align-items-center gap-2 fs-6 fw-bold mb-3'>
                <span className={`${styles.selo} ${styles.seloLme}`}>
                  {t('prescricao.selo.lme')}
                </span>
                {nomes(prescricoesLme, 'lme')}
              </legend>
              <FormSelect
                formik={formik}
                name='cidLme'
                inputId='sus-cid-lme'
                label={t('prescricao.sus.cid')}
                placeholder={t('prescricao.sus.cid_placeholder')}
                fetchOptions={OptionsHelper.getOptionsCids}
                isDisabled={formik.isSubmitting}
                className='mb-3'
                isRequired
              />
              <FormTextArea
                formik={formik}
                name='anamneseLme'
                label={t('prescricao.sus.anamnese')}
                isDisabled={formik.isSubmitting}
                className='mb-3'
                isRequired
              />
              {medicamentosLme.map((prescrito) => {
                const meses = formik.values.mesesLme[String(prescrito.id)] ?? []
                return (
                  <fieldset key={prescrito.id} className='mb-4'>
                    <legend className='fs-6 fw-bold mb-2'>
                      {t('prescricao.sus.meses_lme')} · {prescrito.medicamento?.nome}
                    </legend>
                    <div className={styles.mesesLme}>
                      {meses.map((_, mes) => (
                        <FormInput
                          key={mes}
                          formik={formik}
                          type='number'
                          name={`mesesLme.${prescrito.id}.${mes}`}
                          label={t('prescricao.campo.mes', { mes: mes + 1 })}
                          isDisabled={formik.isSubmitting}
                          isRequired={mes === 0}
                        />
                      ))}
                    </div>
                    <div className='d-flex flex-wrap align-items-center gap-3 mt-2'>
                      <span className={`fs-8 ${styles.textoSecundario}`}>
                        {t('prescricao.sus.meses_lme_dica')}
                      </span>
                      {String(meses[0] ?? '').trim() !== '' && (
                        <Button
                          size='btn-sm'
                          variant='secondary-outline'
                          className='ms-auto'
                          disabled={formik.isSubmitting}
                          onClick={() =>
                            formik.setFieldValue(
                              `mesesLme.${prescrito.id}`,
                              Array(MESES_LME).fill(meses[0])
                            )
                          }
                        >
                          {t('prescricao.campo.repetir_mes_1')}
                        </Button>
                      )}
                    </div>
                  </fieldset>
                )
              })}
              <FormRadio<boolean>
                formik={formik}
                name='temTratamento'
                label={t('prescricao.sus.tratamento_pergunta')}
                isDisabled={formik.isSubmitting}
                isRequired
                isInline
                className='mb-3'
                options={[
                  { id: 1, descricao: t('common.yes'), value: true },
                  { id: 2, descricao: t('common.no'), value: false },
                ]}
              />
              {formik.values.temTratamento && (
                <FormTextArea
                  formik={formik}
                  name='tratamento'
                  label={t('prescricao.sus.tratamento_qual')}
                  isDisabled={formik.isSubmitting}
                  className='mb-3'
                  isRequired
                />
              )}
              {/* Opcional: sem modelo para o CID, a lista vem vazia e nada é exigido. */}
              {!!idCidLme && (
                <FormSelect
                  key={idCidLme}
                  formik={formik}
                  name='modeloTermo'
                  inputId='sus-modelo-termo'
                  label={t('prescricao.sus.modelo_termo')}
                  placeholder={t('prescricao.sus.modelo_termo_placeholder')}
                  infoText={t('prescricao.sus.modelo_termo_dica')}
                  fetchOptions={OptionsHelper.getOptionsModelos}
                  fetchOptionsParams={parametrosModelo}
                  isDisabled={formik.isSubmitting}
                  isClearable
                />
              )}
            </fieldset>
          )}

          {temApac && (
            <fieldset className={styles.caixaApac}>
              <legend className='float-start w-100 d-flex flex-wrap align-items-center gap-2 fs-6 fw-bold mb-3'>
                <span className={`${styles.selo} ${styles.seloApac}`}>
                  {t('prescricao.selo.apac')}
                </span>
                {nomes(prescricoesApac, 'apac')}
              </legend>
              {temLme && (
                <FormCheckBox
                  formik={formik}
                  name='reusar'
                  label={t('prescricao.sus.reusar')}
                  isDisabled={formik.isSubmitting}
                  className='mb-3'
                />
              )}
              {!formik.values.reusar && (
                <>
                  <FormSelect
                    formik={formik}
                    name='cidApac'
                    inputId='sus-cid-apac'
                    label={t('prescricao.sus.cid')}
                    placeholder={t('prescricao.sus.cid_placeholder')}
                    fetchOptions={OptionsHelper.getOptionsCids}
                    isDisabled={formik.isSubmitting}
                    className='mb-3'
                    isRequired
                  />
                  <FormTextArea
                    formik={formik}
                    name='anamneseApac'
                    label={t('prescricao.sus.anamnese')}
                    isDisabled={formik.isSubmitting}
                    className='mb-3'
                    isRequired
                  />
                </>
              )}
              <div className='row g-3'>
                {examesApac.map((exame) => (
                  <div key={exame.id} className='col-md-6'>
                    <FormInput
                      formik={formik}
                      type='number'
                      name={`quantidades.${exame.id}`}
                      label={t('prescricao.sus.quantidade_apac', {
                        exame: exame.exame?.nome ?? '',
                      })}
                      isDisabled={formik.isSubmitting}
                      isRequired
                    />
                  </div>
                ))}
              </div>
            </fieldset>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className='btn-secondary' onClick={onDepois} disabled={formik.isSubmitting}>
            {t('prescricao.sus.depois')}
          </Button>
          <Button
            type={ButtonType.Submit}
            className='btn-primary'
            disabled={formik.isSubmitting || !tipo}
          >
            {formik.isSubmitting ? t('prescricao.sus.gerando') : rotuloSubmit}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
